import { LayoutGrid, Pencil, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Progress } from "~/components/ui/progress";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import type { Rack, RackInventoryData } from "~/types/wms";

import { MiniRackGrid } from "./MiniRackGrid";

interface RackListPanelProps {
  racks: RackInventoryData[];
  selectedRackId: string | null;
  selectedPositionId: string | null;
  onSelectRack: (rackId: string) => void;
  onPositionClick: (positionId: string) => void;
  onEditRack?: (rack: Rack) => void;
  onDeleteRack?: (rack: Rack) => void;
  hallwayCode?: string;
  isLoading?: boolean;
}

export function RackListPanel({
  racks,
  selectedRackId,
  selectedPositionId,
  onSelectRack,
  onPositionClick,
  onEditRack,
  onDeleteRack,
  hallwayCode,
  isLoading,
}: RackListPanelProps) {
  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <LayoutGrid className="h-4 w-4" />
            Racks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-40 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!hallwayCode) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <LayoutGrid className="h-4 w-4" />
            Racks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <LayoutGrid className="h-10 w-10 text-muted-foreground/50" />
            <p className="mt-2 text-sm text-muted-foreground">
              Selecciona un pasillo para ver sus racks
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <LayoutGrid className="h-4 w-4" />
          Racks de {hallwayCode} ({racks.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="max-h-[calc(100vh-200px)] overflow-y-auto">
        {racks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <LayoutGrid className="h-10 w-10 text-muted-foreground/50" />
            <p className="mt-2 text-sm text-muted-foreground">
              No hay racks en este pasillo
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {racks.map((rackData, index) => {
              const isSelected = selectedRackId === rackData.rack.id;
              const occupancy = rackData.occupancyPercent;

              return (
                <button
                  key={rackData.rack.id}
                  onClick={() => onSelectRack(rackData.rack.id)}
                  style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                  className={cn(
                    "rounded-lg border p-3 text-left transition-all duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both",
                    "hover:border-accent-foreground/20 hover:bg-accent",
                    isSelected &&
                      "border-primary bg-primary/5 ring-2 ring-primary"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <div className="font-mono text-sm font-semibold">
                          {rackData.rack.code}
                        </div>
                        <div className="flex items-center gap-0.5">
                          {onEditRack && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditRack(rackData.rack);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          )}
                          {onDeleteRack && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteRack(rackData.rack);
                              }}
                            >
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Mini grid preview */}
                    <div className="flex-shrink-0">
                      <MiniRackGrid
                        rackCode=""
                        positions={rackData.positions}
                        selectedPositionId={
                          isSelected ? selectedPositionId : null
                        }
                        onPositionClick={(posId) => {
                          onSelectRack(rackData.rack.id);
                          onPositionClick(posId);
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-2">
                    <Progress value={occupancy} className="h-1.5 flex-1" />
                    <span className="w-10 text-right text-xs text-muted-foreground">
                      {occupancy.toFixed(0)}%
                    </span>
                  </div>

                  <div className="mt-1 text-xs text-muted-foreground">
                    {rackData.occupiedPositions}/{rackData.totalPositions}{" "}
                    posiciones
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
